import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createAreaOverride,
  createRoute,
  createRouteAreaMapping,
  deleteAreaOverride,
  deleteRoute,
  deleteRouteAreaMapping,
  getAreaOverrides,
  getCustomers,
  getRouteAreaMappings,
  getRoutes,
  updateAreaOverride,
  updateRoute,
  updateRouteAreaMapping,
} from "../../api/client.js";
import { useSortableData } from "../../hooks/useSortableData.js";
import { ConfirmDialog, Dialog } from "../Dialog.jsx";
import { SortableHeader } from "../SortableHeader.jsx";
import {
  DataMasterActiveFilters,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterRecordFooter,
  DataMasterRowActions,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
} from "./DataMasterLayout.jsx";

const EMPTY_ROUTE = {
  active: true,
  color: "#0B5D5E",
  routeCode: "",
  routeName: "",
};

const EMPTY_MAPPING = {
  active: true,
  areaName: "",
  routeCode: "",
};

const EMPTY_OVERRIDE = {
  active: true,
  customerId: null,
  customerName: "",
  customerNo: "",
  notes: "",
  overrideArea: "",
};

function ActiveBadge({ active }) {
  return (
    <span className={`master-active master-active--${active ? "yes" : "no"}`}>
      {active ? "Aktif" : "Nonaktif"}
    </span>
  );
}

function ActiveSelect({ onChange, value }) {
  return (
    <select
      aria-label="Status"
      onChange={(event) => onChange(event.target.value === "active")}
      value={value ? "active" : "inactive"}
    >
      <option value="active">Aktif</option>
      <option value="inactive">Nonaktif</option>
    </select>
  );
}

function ModalActions({ busy, cancelLabel = "Batal", onCancel, submitLabel = "Simpan" }) {
  return (
    <footer className="dialog-card__actions">
      <button className="secondary-button" disabled={busy} onClick={onCancel} type="button">
        {cancelLabel}
      </button>
      <button className="primary-button" disabled={busy} type="submit">
        {busy ? "Menyimpan..." : submitLabel}
      </button>
    </footer>
  );
}

function CustomerLookup({
  customerNo,
  disabled = false,
  onClear,
  onSelect,
  value,
}) {
  const [query, setQuery] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    if (!open) setQuery(value || "");
  }, [open, value]);

  useEffect(() => {
    if (!open || disabled) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setSearchError("");

      try {
        const payload = await getCustomers({
          page: 1,
          pageSize: 50,
          search: query.trim(),
        });

        if (!cancelled) setOptions(payload.customers || []);
      } catch (requestError) {
        if (!cancelled) {
          setOptions([]);
          setSearchError(requestError.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [disabled, open, query]);

  function handleInputChange(event) {
    const nextValue = event.target.value;
    setQuery(nextValue);
    setOpen(true);
    if (nextValue !== value) onClear();
  }

  function selectCustomer(customer) {
    onSelect(customer);
    setQuery(customer.customerName || "");
    setOpen(false);
  }

  return (
    <div className="customer-lookup">
      <input
        aria-autocomplete="list"
        aria-expanded={open}
        aria-label="Nama Pelanggan"
        autoComplete="off"
        disabled={disabled}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        placeholder="Cari Nama Pelanggan"
        role="combobox"
        value={query}
      />

      {customerNo && (
        <span className="customer-lookup__selected-number">{customerNo}</span>
      )}

      {open && !disabled && (
        <div className="customer-lookup__menu" role="listbox">
          {loading && <div className="customer-lookup__message">Mencari pelanggan...</div>}
          {!loading && searchError && (
            <div className="customer-lookup__message customer-lookup__message--error">
              {searchError}
            </div>
          )}
          {!loading &&
            !searchError &&
            options.map((customer) => (
              <button
                key={customer.id}
                onClick={() => selectCustomer(customer)}
                onMouseDown={(event) => event.preventDefault()}
                role="option"
                type="button"
              >
                <strong>{customer.customerName}</strong>
                <span>
                  {customer.customerNo || "Tanpa nomor"}
                  {customer.billingCity ? ` · ${customer.billingCity}` : ""}
                </span>
              </button>
            ))}
          {!loading && !searchError && options.length === 0 && (
            <div className="customer-lookup__message">Pelanggan tidak ditemukan.</div>
          )}
        </div>
      )}
    </div>
  );
}

function RouteForm({ disabledCode = false, form, onChange }) {
  return (
    <div className="dialog-card__body dialog-form__grid">
      <label>
        <span>Kode rute</span>
        <input
          disabled={disabledCode}
          onChange={(event) => onChange({ ...form, routeCode: event.target.value })}
          required
          value={form.routeCode}
        />
      </label>
      <label>
        <span>Nama rute</span>
        <input
          onChange={(event) => onChange({ ...form, routeName: event.target.value })}
          required
          value={form.routeName}
        />
      </label>
      <label>
        <span>Warna rute</span>
        <input
          onChange={(event) => onChange({ ...form, color: event.target.value })}
          type="color"
          value={form.color || "#0B5D5E"}
        />
      </label>
      <label>
        <span>Status</span>
        <ActiveSelect onChange={(active) => onChange({ ...form, active })} value={form.active} />
      </label>
    </div>
  );
}

export function RoutesTab() {
  const EMPTY_FILTERS = { search: "", status: "" };
  const [rows, setRows] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ROUTE);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const mappingUsage = useMemo(() => {
    const counts = new Map();
    mappings.forEach((mapping) => {
      const key = String(mapping.routeCode || "").toUpperCase();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [mappings]);

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        !search ||
        [row.routeCode, row.routeName]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search));
      const matchesStatus =
        !filters.status ||
        (filters.status === "ACTIVE" ? row.active : !row.active);
      return matchesSearch && matchesStatus;
    });
  }, [rows, filters]);

  const { sortedItems, sort, toggleSort } = useSortableData(filteredRows, "routeName");

  const summary = useMemo(() => ({
    total: filteredRows.length,
    active: filteredRows.filter((row) => row.active).length,
    inactive: filteredRows.filter((row) => !row.active).length,
    used: filteredRows.filter((row) => mappingUsage.has(String(row.routeCode || "").toUpperCase())).length,
  }), [filteredRows, mappingUsage]);

  const load = useCallback(async () => {
    const [routePayload, mappingPayload] = await Promise.all([
      getRoutes(),
      getRouteAreaMappings(),
    ]);
    setRows(routePayload.routes || []);
    setMappings(mappingPayload.mappings || []);
  }, []);

  useEffect(() => {
    load().catch((requestError) => setError(requestError.message));
  }, [load]);

  function applyFilters(event) {
    event.preventDefault();
    setFilters({ ...draftFilters, search: draftFilters.search.trim() });
  }

  function clearFilters() {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  }

  async function submitCreate(event) {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const response = await createRoute(form);
      setMessage(response.message);
      setForm(EMPTY_ROUTE);
      setCreateOpen(false);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitEdit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const response = await updateRoute(editRow.id, editRow);
      setMessage(response.message);
      setEditRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row) {
    setError("");

    try {
      const response = await updateRoute(row.id, {
        ...row,
        active: !row.active,
      });
      setMessage(response.message);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError("");

    try {
      const response = await deleteRoute(deleteRow.id);
      setMessage(response.message);
      setDeleteRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setDeleteRow(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={() => {
            setForm(EMPTY_ROUTE);
            setCreateOpen(true);
          }}
          type="button"
        >
          Tambah Rute
        </button>
      }
      error={error}
      message={message}
      subtitle="Kelola pengelompokan rute yang digunakan untuk area dan Sales Order."
      title="Rute"
    >
      <DataMasterKpiStrip label="Ringkasan rute pengiriman">
        <DataMasterKpiCard icon="database" label="Total rute" value={summary.total} />
        <DataMasterKpiCard icon="check" label="Rute aktif" tone="success" value={summary.active} />
        <DataMasterKpiCard icon="close" label="Nonaktif" value={summary.inactive} />
        <DataMasterKpiCard
          helper="Memiliki Mapping Area"
          icon="map"
          label="Rute digunakan"
          tone="info"
          value={summary.used}
        />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-location-filter-bar dm-location-filter-bar--compact" onSubmit={applyFilters}>
        <input
          aria-label="Cari rute"
          onChange={(event) => setDraftFilters((current) => ({ ...current, search: event.target.value }))}
          placeholder="Cari kode atau nama rute"
          type="search"
          value={draftFilters.search}
        />
        <select
          aria-label="Status rute"
          onChange={(event) => setDraftFilters((current) => ({ ...current, status: event.target.value }))}
          value={draftFilters.status}
        >
          <option value="">Semua status</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <button className="secondary-button" type="submit">Terapkan</button>
        <button className="text-button" onClick={clearFilters} type="button">Bersihkan</button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          { label: "Status", value: { ACTIVE: "Aktif", INACTIVE: "Nonaktif" }[filters.status] || "" },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell emptyText="Belum ada rute yang sesuai filter." loading={false} mode="compact" rowCount={sortedItems.length}>
        <table className="master-table master-table--routes-compact">
          <thead>
            <tr>
              <SortableHeader column="routeName" sort={sort} toggleSort={toggleSort}>Rute</SortableHeader>
              <SortableHeader column="color" sort={sort} toggleSort={toggleSort}>Identitas warna</SortableHeader>
              <th>Mapping Area</th>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>Status</SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((row) => {
              const usageCount = mappingUsage.get(String(row.routeCode || "").toUpperCase()) || 0;
              return (
                <tr key={row.id}>
                  <td>
                    <div className="dm-cell-stack dm-cell-stack--primary">
                      <strong>{row.routeName}</strong>
                      <span>{row.routeCode}</span>
                    </div>
                  </td>
                  <td>
                    <div className="dm-route-color-cell">
                      <span className="route-color" style={{ backgroundColor: row.color || "#94A3B8" }} />
                      <span>{row.color || "Belum ditentukan"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="dm-cell-stack">
                      <strong>{usageCount} area</strong>
                      <span>{usageCount ? "Sedang digunakan" : "Belum digunakan"}</span>
                    </div>
                  </td>
                  <td><ActiveBadge active={row.active} /></td>
                  <td className="sticky-action-cell master-action-cell">
                    <DataMasterRowActions
                      active={row.active}
                      onDelete={() => setDeleteRow(row)}
                      onEdit={() => setEditRow({ ...row })}
                      onToggle={() => toggle(row)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DataMasterTableShell>
      <DataMasterRecordFooter count={sortedItems.length} />

      {createOpen && (
        <Dialog onClose={() => setCreateOpen(false)} title="Tambah Rute">
          <form className="dialog-form" onSubmit={submitCreate}>
            <RouteForm form={form} onChange={setForm} />
            <ModalActions busy={busy} onCancel={() => setCreateOpen(false)} submitLabel="Tambah Rute" />
          </form>
        </Dialog>
      )}

      {editRow && (
        <Dialog onClose={() => setEditRow(null)} title={`Edit Rute ${editRow.routeCode}`}>
          <form className="dialog-form" onSubmit={submitEdit}>
            <RouteForm disabledCode form={editRow} onChange={setEditRow} />
            <ModalActions busy={busy} onCancel={() => setEditRow(null)} />
          </form>
        </Dialog>
      )}

      {deleteRow && (
        <ConfirmDialog
          busy={busy}
          description={`Hapus rute "${deleteRow.routeName}" (${deleteRow.routeCode})? Tindakan ini tidak dapat dibatalkan.`}
          onCancel={() => setDeleteRow(null)}
          onConfirm={confirmDelete}
        />
      )}
    </DataMasterSection>
  );
}

function routeOptions(routes) {
  return routes.map((route) => (
    <option key={route.id} value={route.routeCode}>
      {route.routeCode} · {route.routeName}
    </option>
  ));
}

function MappingForm({ form, onChange, routes }) {
  return (
    <div className="dialog-card__body dialog-form__grid">
      <label>
        <span>Area</span>
        <input
          onChange={(event) => onChange({ ...form, areaName: event.target.value })}
          required
          value={form.areaName}
        />
      </label>
      <label>
        <span>Rute</span>
        <select
          onChange={(event) => onChange({ ...form, routeCode: event.target.value })}
          required
          value={form.routeCode}
        >
          <option value="">Pilih rute</option>
          {routeOptions(routes)}
        </select>
      </label>
      <label>
        <span>Status</span>
        <ActiveSelect onChange={(active) => onChange({ ...form, active })} value={form.active} />
      </label>
    </div>
  );
}

function buildActiveAreaOptions(mappings = []) {
  const seen = new Set();

  return mappings
    .filter((mapping) => mapping.active !== false && mapping.areaName)
    .map((mapping) => ({
      area: String(mapping.areaName || "").trim(),
      routeCode: String(mapping.routeCode || "").trim(),
      routeName: String(mapping.routeName || mapping.routeCode || "").trim(),
    }))
    .filter((mapping) => {
      const key = mapping.area.toLowerCase();
      if (!mapping.area || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => left.area.localeCompare(right.area, "id"));
}

export function RouteMappingsTab() {
  const EMPTY_FILTERS = { search: "", routeCode: "", status: "" };
  const [rows, setRows] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_MAPPING);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        !search ||
        [row.areaName, row.routeCode, row.routeName]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search));
      const matchesRoute = !filters.routeCode || row.routeCode === filters.routeCode;
      const matchesStatus =
        !filters.status ||
        (filters.status === "ACTIVE" ? row.active : !row.active);
      return matchesSearch && matchesRoute && matchesStatus;
    });
  }, [rows, filters]);

  const { sortedItems, sort, toggleSort } = useSortableData(filteredRows, "areaName");

  const summary = useMemo(() => ({
    total: filteredRows.length,
    active: filteredRows.filter((row) => row.active).length,
    inactive: filteredRows.filter((row) => !row.active).length,
    routesUsed: new Set(filteredRows.filter((row) => row.active).map((row) => row.routeCode)).size,
  }), [filteredRows]);

  const load = useCallback(async () => {
    const [mappingPayload, routePayload] = await Promise.all([
      getRouteAreaMappings(),
      getRoutes(),
    ]);
    setRows(mappingPayload.mappings || []);
    setRoutes(routePayload.routes || []);
  }, []);

  useEffect(() => {
    load().catch((requestError) => setError(requestError.message));
  }, [load]);

  function applyFilters(event) {
    event.preventDefault();
    setFilters({ ...draftFilters, search: draftFilters.search.trim() });
  }

  function clearFilters() {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  }

  async function submitCreate(event) {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const response = await createRouteAreaMapping(form);
      setMessage(response.message);
      setForm(EMPTY_MAPPING);
      setCreateOpen(false);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitEdit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const response = await updateRouteAreaMapping(editRow.id, editRow);
      setMessage(response.message);
      setEditRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row) {
    setError("");

    try {
      const response = await updateRouteAreaMapping(row.id, {
        ...row,
        active: !row.active,
      });
      setMessage(response.message);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError("");

    try {
      const response = await deleteRouteAreaMapping(deleteRow.id);
      setMessage(response.message);
      setDeleteRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setDeleteRow(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={() => {
            setForm(EMPTY_MAPPING);
            setCreateOpen(true);
          }}
          type="button"
        >
          Tambah Mapping
        </button>
      }
      error={error}
      message={message}
      subtitle="Hubungkan area pelanggan dengan rute pengiriman aktif."
      title="Mapping Area"
    >
      <DataMasterKpiStrip label="Ringkasan mapping area">
        <DataMasterKpiCard icon="database" label="Total mapping" value={summary.total} />
        <DataMasterKpiCard icon="check" label="Mapping aktif" tone="success" value={summary.active} />
        <DataMasterKpiCard icon="close" label="Nonaktif" value={summary.inactive} />
        <DataMasterKpiCard icon="map" label="Rute digunakan" tone="info" value={summary.routesUsed} />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-location-filter-bar dm-location-filter-bar--compact" onSubmit={applyFilters}>
        <input
          aria-label="Cari mapping area"
          onChange={(event) => setDraftFilters((current) => ({ ...current, search: event.target.value }))}
          placeholder="Cari area, kode rute, atau nama rute"
          type="search"
          value={draftFilters.search}
        />
        <select
          aria-label="Filter rute mapping"
          onChange={(event) => setDraftFilters((current) => ({ ...current, routeCode: event.target.value }))}
          value={draftFilters.routeCode}
        >
          <option value="">Semua rute</option>
          {routes.map((route) => (
            <option key={route.id} value={route.routeCode}>{route.routeName}</option>
          ))}
        </select>
        <select
          aria-label="Status mapping area"
          onChange={(event) => setDraftFilters((current) => ({ ...current, status: event.target.value }))}
          value={draftFilters.status}
        >
          <option value="">Semua status</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <button className="secondary-button" type="submit">Terapkan</button>
        <button className="text-button" onClick={clearFilters} type="button">Bersihkan</button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          { label: "Rute", value: routes.find((route) => route.routeCode === filters.routeCode)?.routeName || "" },
          { label: "Status", value: { ACTIVE: "Aktif", INACTIVE: "Nonaktif" }[filters.status] || "" },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell emptyText="Belum ada mapping area yang sesuai filter." loading={false} mode="compact" rowCount={sortedItems.length}>
        <table className="master-table master-table--route-mappings-compact">
          <thead>
            <tr>
              <SortableHeader column="areaName" sort={sort} toggleSort={toggleSort}>Area</SortableHeader>
              <SortableHeader column="routeName" sort={sort} toggleSort={toggleSort}>Rute tujuan</SortableHeader>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>Status</SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((row) => (
              <tr key={row.id}>
                <td>
                  <div className="dm-cell-stack dm-cell-stack--primary">
                    <strong>{row.areaName}</strong>
                    <span>Area pelanggan</span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{row.routeName || row.routeCode}</strong>
                    <span>{row.routeCode}</span>
                  </div>
                </td>
                <td><ActiveBadge active={row.active} /></td>
                <td className="sticky-action-cell master-action-cell">
                  <DataMasterRowActions
                    active={row.active}
                    onDelete={() => setDeleteRow(row)}
                    onEdit={() => setEditRow({ ...row })}
                    onToggle={() => toggle(row)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataMasterTableShell>
      <DataMasterRecordFooter count={sortedItems.length} />

      {createOpen && (
        <Dialog onClose={() => setCreateOpen(false)} title="Tambah Mapping Area">
          <form className="dialog-form" onSubmit={submitCreate}>
            <MappingForm form={form} onChange={setForm} routes={routes} />
            <ModalActions busy={busy} onCancel={() => setCreateOpen(false)} submitLabel="Tambah Mapping" />
          </form>
        </Dialog>
      )}

      {editRow && (
        <Dialog onClose={() => setEditRow(null)} title={`Edit Mapping ${editRow.areaName}`}>
          <form className="dialog-form" onSubmit={submitEdit}>
            <MappingForm form={editRow} onChange={setEditRow} routes={routes} />
            <ModalActions busy={busy} onCancel={() => setEditRow(null)} />
          </form>
        </Dialog>
      )}

      {deleteRow && (
        <ConfirmDialog
          busy={busy}
          description={`Hapus mapping area "${deleteRow.areaName}" ke rute ${deleteRow.routeName || deleteRow.routeCode}? Tindakan ini tidak dapat dibatalkan.`}
          onCancel={() => setDeleteRow(null)}
          onConfirm={confirmDelete}
        />
      )}
    </DataMasterSection>
  );
}

function OverrideForm({
  areaOptions = [],
  clearCustomer,
  form,
  onChange,
  selectCustomer,
}) {
  const selectedArea =
    areaOptions.find((option) => option.area === form.overrideArea) || null;
  const hasLegacyArea =
    Boolean(form.overrideArea) && !selectedArea;

  return (
    <div className="dialog-card__body dialog-form__grid">
      <div className="dialog-field dialog-form__full">
        <span>Nama Pelanggan</span>
        <CustomerLookup
          customerNo={form.customerNo}
          onClear={clearCustomer}
          onSelect={selectCustomer}
          value={form.customerName}
        />
      </div>
      <label>
        <span>Normalisasi Area</span>
        <select
          onChange={(event) => onChange({ ...form, overrideArea: event.target.value })}
          required
          value={form.overrideArea}
        >
          <option value="">Pilih area dari Mapping Area</option>
          {hasLegacyArea && (
            <option value={form.overrideArea}>
              {form.overrideArea} (tidak aktif)
            </option>
          )}
          {areaOptions.map((option) => (
            <option key={option.area} value={option.area}>
              {option.area}
            </option>
          ))}
        </select>
      </label>
      <div className="dialog-field">
        <span>Rute hasil mapping</span>
        <input
          className="master-readonly-input"
          readOnly
          value={
            selectedArea
              ? `${selectedArea.routeCode} · ${selectedArea.routeName}`
              : hasLegacyArea
                ? "Area tersimpan tidak aktif di Mapping Area"
                : "Pilih Normalisasi Area"
          }
        />
      </div>
      <label>
        <span>Catatan</span>
        <input
          onChange={(event) => onChange({ ...form, notes: event.target.value })}
          value={form.notes || ""}
        />
      </label>
      <label>
        <span>Status</span>
        <ActiveSelect onChange={(active) => onChange({ ...form, active })} value={form.active} />
      </label>
    </div>
  );
}

export function AreaOverridesTab() {
  const EMPTY_FILTERS = { search: "", area: "", status: "" };
  const [rows, setRows] = useState([]);
  const [areaMappings, setAreaMappings] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_OVERRIDE);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    const [overridePayload, mappingPayload] = await Promise.all([
      getAreaOverrides(),
      getRouteAreaMappings(),
    ]);
    setRows(overridePayload.overrides || []);
    setAreaMappings(mappingPayload.mappings || []);
  }, []);

  const areaOptions = useMemo(
    () => buildActiveAreaOptions(areaMappings),
    [areaMappings],
  );
  const activeAreaSet = useMemo(
    () => new Set(areaOptions.map((option) => option.area.toLowerCase())),
    [areaOptions],
  );

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        !search ||
        [row.customerNo, row.customerName, row.overrideArea, row.notes]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(search));
      const matchesArea = !filters.area || row.overrideArea === filters.area;
      const matchesStatus =
        !filters.status ||
        (filters.status === "ACTIVE" ? row.active : !row.active);
      return matchesSearch && matchesArea && matchesStatus;
    });
  }, [rows, filters]);

  const { sortedItems, sort, toggleSort } = useSortableData(filteredRows, "customerName");

  const summary = useMemo(() => ({
    total: filteredRows.length,
    active: filteredRows.filter((row) => row.active).length,
    inactive: filteredRows.filter((row) => !row.active).length,
    customers: new Set(filteredRows.map((row) => row.customerId || row.customerNo || row.customerName)).size,
  }), [filteredRows]);

  useEffect(() => {
    load().catch((requestError) => setError(requestError.message));
  }, [load]);

  function applyFilters(event) {
    event.preventDefault();
    setFilters({ ...draftFilters, search: draftFilters.search.trim() });
  }

  function clearFilters() {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  }

  function validateOverride(value) {
    if (!value.customerId) return "Pilih Nama Pelanggan dari master Pelanggan.";
    if (!String(value.overrideArea || "").trim()) return "Normalisasi Area wajib diisi.";
    const normalizedArea = String(value.overrideArea || "").trim().toLowerCase();
    const unchangedLegacyArea =
      value.originalOverrideArea &&
      String(value.originalOverrideArea).trim().toLowerCase() === normalizedArea;

    if (!activeAreaSet.has(normalizedArea) && !unchangedLegacyArea) {
      return "Pilih Normalisasi Area dari Mapping Area aktif.";
    }
    return "";
  }

  function buildPayload(value) {
    return {
      active: value.active,
      customerId: value.customerId,
      customerName: value.customerName,
      notes: String(value.notes || "").trim(),
      overrideArea: value.overrideArea.trim(),
    };
  }

  function selectCustomerFor(setter) {
    return (customer) =>
      setter((current) => ({
        ...current,
        customerId: customer.id,
        customerName: customer.customerName || "",
        customerNo: customer.customerNo || "",
      }));
  }

  function clearCustomerFor(setter) {
    return () =>
      setter((current) => ({
        ...current,
        customerId: null,
        customerName: "",
        customerNo: "",
      }));
  }

  async function submitCreate(event) {
    event.preventDefault();
    const validationError = validateOverride(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response = await createAreaOverride(buildPayload(form));
      setMessage(response.message);
      setForm(EMPTY_OVERRIDE);
      setCreateOpen(false);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitEdit(event) {
    event.preventDefault();
    const validationError = validateOverride(editRow);
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setMessage("");
    setError("");

    try {
      const response = await updateAreaOverride(editRow.id, buildPayload(editRow));
      setMessage(response.message);
      setEditRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row) {
    setError("");

    try {
      const response = await updateAreaOverride(row.id, buildPayload({
        ...row,
        active: !row.active,
      }));
      setMessage(response.message);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setError("");

    try {
      const response = await deleteAreaOverride(deleteRow.id);
      setMessage(response.message);
      setDeleteRow(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setDeleteRow(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={() => {
            setForm(EMPTY_OVERRIDE);
            setCreateOpen(true);
          }}
          type="button"
        >
          Tambah Normalisasi Area
        </button>
      }
      error={error}
      message={message}
      subtitle="Tetapkan area operasional pelanggan sebelum proses Mapping Area menentukan rute."
      title="Normalisasi Area"
    >
      <DataMasterKpiStrip label="Ringkasan normalisasi area">
        <DataMasterKpiCard icon="database" label="Total normalisasi" value={summary.total} />
        <DataMasterKpiCard icon="check" label="Aktif" tone="success" value={summary.active} />
        <DataMasterKpiCard icon="close" label="Nonaktif" value={summary.inactive} />
        <DataMasterKpiCard icon="user" label="Pelanggan terdampak" tone="info" value={summary.customers} />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-location-filter-bar dm-location-filter-bar--compact" onSubmit={applyFilters}>
        <input
          aria-label="Cari normalisasi area"
          onChange={(event) => setDraftFilters((current) => ({ ...current, search: event.target.value }))}
          placeholder="Cari nomor, pelanggan, area, atau catatan"
          type="search"
          value={draftFilters.search}
        />
        <select
          aria-label="Area hasil normalisasi"
          onChange={(event) => setDraftFilters((current) => ({ ...current, area: event.target.value }))}
          value={draftFilters.area}
        >
          <option value="">Semua area</option>
          {areaOptions.map((option) => (
            <option key={option.area} value={option.area}>{option.area}</option>
          ))}
        </select>
        <select
          aria-label="Status normalisasi area"
          onChange={(event) => setDraftFilters((current) => ({ ...current, status: event.target.value }))}
          value={draftFilters.status}
        >
          <option value="">Semua status</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>
        <button className="secondary-button" type="submit">Terapkan</button>
        <button className="text-button" onClick={clearFilters} type="button">Bersihkan</button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          { label: "Area", value: filters.area },
          { label: "Status", value: { ACTIVE: "Aktif", INACTIVE: "Nonaktif" }[filters.status] || "" },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell emptyText="Belum ada normalisasi area yang sesuai filter." loading={false} mode="compact" rowCount={sortedItems.length}>
        <table className="master-table master-table--area-overrides-compact">
          <thead>
            <tr>
              <SortableHeader column="customerName" sort={sort} toggleSort={toggleSort}>Pelanggan</SortableHeader>
              <SortableHeader column="overrideArea" sort={sort} toggleSort={toggleSort}>Area hasil normalisasi</SortableHeader>
              <SortableHeader column="notes" sort={sort} toggleSort={toggleSort}>Catatan</SortableHeader>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>Status</SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((row) => {
              const route = areaOptions.find((option) => option.area === row.overrideArea);
              return (
                <tr key={row.id}>
                  <td>
                    <div className="dm-cell-stack dm-cell-stack--primary">
                      <strong>{row.customerName}</strong>
                      <span>{row.customerNo || "Tanpa nomor pelanggan"}</span>
                    </div>
                  </td>
                  <td>
                    <div className="dm-cell-stack">
                      <strong>{row.overrideArea}</strong>
                      <span>{route ? `${route.routeCode} · ${route.routeName}` : "Mapping tidak aktif"}</span>
                    </div>
                  </td>
                  <td>{row.notes || "Tanpa catatan"}</td>
                  <td><ActiveBadge active={row.active} /></td>
                  <td className="sticky-action-cell master-action-cell">
                    <DataMasterRowActions
                      active={row.active}
                      onDelete={() => setDeleteRow(row)}
                      onEdit={() =>
                        setEditRow({
                          ...row,
                          customerId: row.customerId || null,
                          originalOverrideArea: row.overrideArea || "",
                        })
                      }
                      onToggle={() => toggle(row)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DataMasterTableShell>
      <DataMasterRecordFooter count={sortedItems.length} />

      {createOpen && (
        <Dialog onClose={() => setCreateOpen(false)} title="Tambah Normalisasi Area">
          <form className="dialog-form" onSubmit={submitCreate}>
            <OverrideForm
              areaOptions={areaOptions}
              clearCustomer={clearCustomerFor(setForm)}
              form={form}
              onChange={setForm}
              selectCustomer={selectCustomerFor(setForm)}
            />
            <ModalActions busy={busy} onCancel={() => setCreateOpen(false)} submitLabel="Tambah Normalisasi Area" />
          </form>
        </Dialog>
      )}

      {editRow && (
        <Dialog onClose={() => setEditRow(null)} title={`Edit Normalisasi Area ${editRow.customerName}`}>
          <form className="dialog-form" onSubmit={submitEdit}>
            <OverrideForm
              areaOptions={areaOptions}
              clearCustomer={clearCustomerFor(setEditRow)}
              form={editRow}
              onChange={setEditRow}
              selectCustomer={selectCustomerFor(setEditRow)}
            />
            <ModalActions busy={busy} onCancel={() => setEditRow(null)} />
          </form>
        </Dialog>
      )}

      {deleteRow && (
        <ConfirmDialog
          busy={busy}
          description={`Hapus normalisasi area untuk "${deleteRow.customerName}"? Tindakan ini tidak dapat dibatalkan.`}
          onCancel={() => setDeleteRow(null)}
          onConfirm={confirmDelete}
        />
      )}
    </DataMasterSection>
  );
}
